<template>
    <div
        :class="['relative', containerClasses]"
        @mouseenter="hoverActive = true"
        @mouseleave="hoverActive = false"
    >
        <!-- Edit toggle button shown only when live designer is active -->
        <edit-toggle-button
            v-if="portalContentAndDesignStore.liveDesignerEnabled && (isEditing || hoverActive)"
            :is-editing="isEditing"
            @toggle="toggleEdit"
            class="transition-opacity duration-200"
        />

        <!-- Display mode -->
        <component
            v-if="!isEditing"
            :is="textType"
            :for="textType === 'label' ? forAttribute : null"
            :href="href"
            :target="textType === 'a' ? linkTarget : null"
            :class="textFieldClasses"
            v-bind="attrs"
        >
            <template v-if="slots['custom-content']">
                <slot name="custom-content" />
            </template>
            <template v-else>
                <icon
                    v-if="iconName"
                    :class="iconClasses"
                    :name="iconName"
                    color="currentColor"
                    :size="iconSize"
                />
                {{ modelValue ?? '' }}
            </template>
        </component>

        <!-- Edit mode -->
        <input
            v-else-if="isEditing"
            ref="inputEl"
            :name="name"
            v-model="model"
            :class="inputClasses"
            v-bind="attrs"
        />
    </div>
</template>

<script setup>
import { ref, computed, useAttrs, useSlots} from 'vue';
import { usePortalContentAndDesignStore } from "../../../stores/partners/portalContentAndDesignStore";
import EditToggleButton from "../Pub/components/EditToggleButton.vue";
import {useEditableFocus} from "../../../composables/partners/useEditableFocus";

const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    name: String,
    textType: {
        type: String,
        default: 'h2',
        validator: value => ['h1', 'h2', 'h3', 'h5', 'label', 'p', 'span', 'a', 'custom'].includes(value)
    },
    customClasses: {
        type: [String, Array],
        default: ''
    },
    forAttribute: String,
    href: String,
    linkTarget: {
        type: String,
        default: '_self'
    },
    iconName: String,
    iconRight: Boolean,
    iconSize: {
        type: String,
        default: '16'
    },
    containerClasses: {
        type: [String, Array],
        default: ''
    },
    inputTransparentBackground: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits(['update:modelValue']);

const model = computed({
    get: () => props.modelValue,
    set: (val) => emit('update:modelValue', val)
});

const isEditing = ref(false);
const hoverActive = ref(false);
const inputEl = ref(null);

useEditableFocus(isEditing, inputEl)

const toggleEdit = () => {
    isEditing.value = !isEditing.value
    if (!isEditing.value) {
        hoverActive.value = false
    }
}

const portalContentAndDesignStore = usePortalContentAndDesignStore();
const attrs = useAttrs();
const slots = useSlots();


const textFieldClassesMap = {
    h1: 'text-4xl font-bold',
    h2: 'text-3xl font-semibold',
    h3: 'text-2xl font-medium',
    label: '',
    p: '',
    span: '',
    a: '',
    custom: ''
};

const inputClassesMap = {
    h1: `text-4xl font-bold border border-gray-300 p-2 rounded focus:ring-black ${props.inputTransparentBackground ? 'bg-transparent' : ''}`,
    h2: `text-3xl font-semibold border border-gray-300 p-2 rounded focus:ring-black ${props.inputTransparentBackground ? 'bg-transparent' : ''}`,
    h3: `text-2xl font-medium border border-gray-300 p-2 rounded focus:ring-black ${props.inputTransparentBackground ? 'bg-transparent' : ''}`,
    label: `border border-gray-300 p-1 rounded focus:ring-black ${props.inputTransparentBackground ? 'bg-transparent' : ''}`,
    p: `border border-gray-300 p-1 rounded focus:ring-black ${props.inputTransparentBackground ? 'bg-transparent' : ''}`,
    span: `border border-gray-300 p-1 rounded focus:ring-black ${props.inputTransparentBackground ? 'bg-transparent' : ''}`,
    a: `inline-block border border-gray-300 p-1 rounded focus:ring-black ${props.inputTransparentBackground ? 'bg-transparent' : ''}`,
    custom: ''
};

const textFieldClasses = computed(() => [
    textFieldClassesMap[props.textType],
    props.customClasses,
    attrs.class // include passed class binding
]);

const inputClasses = computed(() => [
    inputClassesMap[props.textType],
    props.customClasses,
    attrs.class // include passed class binding
]);

const iconClasses = computed(() => [
    props.iconRight ? 'ml-1.5 order-last' : 'mr-1.5'
]);
</script>
